import React, { useEffect, useState, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import PaginatedTable from "../components/Table/PaginatedTable";
import APIServicenew from "../utils/APIGeneralService";
import { UserContext } from "../context/UserContextProvider";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { SlEye } from "react-icons/sl";

const TestRunManagement = () => {
  const APIServices = new APIServicenew(process.env.REACT_APP_OTHER_API_URL);
  const { accessToken, logout } = useContext(UserContext);
  const navigate = useNavigate();

  /* ================= STATES ================= */
  const [showModal, setShowModal] = useState(false);

  const [runCases, setRunCases] = useState([]);
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  const [selectedTestRunIds, setSelectedTestRunIds] = useState([]);
  const [executing, setExecuting] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [keyword, setKeyword] = useState("");

  /* ===== Modal Folder States ===== */
  const [folderList, setFolderList] = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  /* ===== Test Cases States ===== */
  const [testCases, setTestCases] = useState([]);
  const [loadingTestCases, setLoadingTestCases] = useState(false);
  const [selectedTestCases, setSelectedTestCases] = useState([]);
  const [runningTests, setRunningTests] = React.useState({});

  const headers = {
    Authorization: `Bearer ${accessToken}`,
  };

  /* ================= Utility Functions ================= */
  const parseFolderIds = (folderIdsString) => {
    if (!folderIdsString) return [];
    return folderIdsString
      .split(',')
      .map(id => id.trim())
      .filter(id => id !== '')
      .map(id => {
        const num = Number(id);
        return isNaN(num) ? 0 : num;
      })
      .filter(id => id > 0);
  };

  /* ================= Checkbox ================= */
  const handleCheckboxChange = (testRunId, checked) => {
    setSelectedTestRunIds((prev) =>
      checked
        ? [...prev, Number(testRunId)]
        : prev.filter((id) => id !== Number(testRunId))
    );
  };

  /* ================= Fetch Test Cases for Selected Folder ================= */
  const fetchTestCases = async (folderId) => {
    if (!folderId) {
      setTestCases([]);
      setSelectedTestCases([]);
      return;
    }

    setLoadingTestCases(true);
    try {
      const response = await APIServices.get(
        `/test-execution/folders/test-cases?folder_id=${folderId}`,
        headers
      );

      if (response?.status === 200 && response.data) {
        setTestCases(response.data.testCases || []);
        // Select all test cases by default
        setSelectedTestCases([]);
      } else {
        setTestCases([]);
        setSelectedTestCases([]);
      }
    } catch (error) {
      console.error("Error fetching test cases:", error);
      setTestCases([]);
      setSelectedTestCases([]);
    }
    setLoadingTestCases(false);
  };

  /* ================= Handle Test Case Checkbox ================= */
  const handleTestCaseCheckboxChange = (testCaseId, checked) => {
    setSelectedTestCases(prev =>
      checked
        ? [...prev, testCaseId]
        : prev.filter(id => id !== testCaseId)
    );
  };

  /* ================= Handle Select All Test Cases ================= */
  const handleSelectAllTestCases = (checked) => {
    if (checked) {
      setSelectedTestCases(testCases.map(tc => tc.id));
    } else {
      setSelectedTestCases([]);
    }
  };

  /* ================= Fetch Run Cases ================= */
  const fetchRunCase = async (page = 1) => {
    setLoading(true);
    try {
      const queryParams = [`limit=${limit}`, `page=${page}`];
      if (keyword) queryParams.push(`folder_search=${encodeURIComponent(keyword)}`);
      const queryString = `?${queryParams.join("&")}`;

      const response = await APIServices.get(
        `/test-execution/runs${queryString}`,
        headers
      );

      if (response?.status === 200) {
        setRunCases(response.data.results || []);
        setTotalItems(response.data.total || 0);
        setTotalPages(response.data.total_pages || 0);
      } else if (response?.status === 401) {
        logout();
        navigate("/");
      } else {
        setRunCases([]);
        setTotalItems(0);
        setTotalPages(0);
      }
    } catch (error) {
      console.error("Error fetching run cases:", error);
      setRunCases([]);
      setTotalItems(0);
      setTotalPages(0);
    }
    setLoading(false);
  };

  /* ================= Fetch Folder List (Modal) ================= */
  const fetchFolders = async () => {
    setLoadingFolders(true);
    try {
      const response = await APIServices.get(
        `admin/folders?limit=10000&page=1&is_active=true`,
        headers
      );

      if (response?.status === 200) {
        setFolderList(response?.data?.data || []);
      } else {
        setFolderList([]);
      }
    } catch (error) {
      console.error("Error fetching folders:", error);
      setFolderList([]);
    }
    setLoadingFolders(false);
  };

  /* ================= Effects ================= */
  useEffect(() => {
    fetchRunCase(currentPage);
  }, [currentPage, limit, keyword]);

  useEffect(() => {
    if (showModal) {
      fetchFolders();
      setSelectedFolderId("");
      setTestCases([]);
      setSelectedTestCases([]);
    }
  }, [showModal]);

  useEffect(() => {
    // Fetch test cases when folder is selected
    if (selectedFolderId) {
      fetchTestCases(selectedFolderId);
    } else {
      setTestCases([]);
      setSelectedTestCases([]);
    }
  }, [selectedFolderId]);

  const handleTestRunAction = async (id, actionType) => {
    try {
      // Set loader only for clicked action
      setRunningTests((prev) => ({
        ...prev,
        [id]: {
          ...prev[id],
          [actionType]: true,
        },
      }));

      const payload = {
        action: actionType, // "pause", "resume", "stop"
        runExecutionId: "RUN-" + id,
        isFolder: "True",
      };

      const response = await APIServices.post(
        "test-execution/action",
        payload,
        headers
      );

      if (response?.status === 200 || response?.status === 201) {
        toast.success(`Test run ${actionType} successfully`);
        fetchRunCase(currentPage);
      } else {
        toast.error(response?.data?.detail ?? `Failed to ${actionType} test run`);
      }
    } catch (error) {
      toast.error(error.message || `Error ${actionType}ing test run`);
      console.error(`Error ${actionType}ing test run:`, error);
    } finally {
      // Remove loader only for that action
      setRunningTests((prev) => ({
        ...prev,
        [id]: {
          ...prev[id],
          [actionType]: false,
        },
      }));
    }
  };


  const columns = [
    {
      dataField: "checkbox",
      text: "",
      formatter: (cell, row) => (
        <input
          type="checkbox"
          className="form-check-input"
          checked={selectedTestRunIds.includes(Number(row.id))}
          onChange={(e) => handleCheckboxChange(row.id, e.target.checked)}
        />
      ),
    },
    {
      dataField: "folder_names",
      text: "Test Folder",
      formatter: (cell) => {
        if (!cell) return "-";
        const folders = cell.split(",").map((f) => f.trim());
        return folders.map((folder, index) => (
          <div key={index} className="folder-item">
            {folder}
          </div>
        ));
      },
    },
    {
      dataField: "created_by_name",
      text: "Test Created By",
      formatter: (cell) => cell || "-",
    },
    {
      dataField: "creation_date",
      text: "Creation Date",
      formatter: (cell) =>
        cell
          ? new Date(cell).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })
          : "-",
    },
    {
      dataField: "creation_date",
      text: "Execution Date",
      formatter: (cell) =>
        cell
          ? new Date(cell).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })
          : "-",
    },
    {
      dataField: "status",
      text: "Status",
      formatter: (cell, row) => {
        const statusToShow = row.executed_status || row.current_status;
        if (!statusToShow) return "-";

        const normalizedStatus = statusToShow
          .toString()
          .toLowerCase()
          .replace(/\s+/g, "_");

        const isInProgress = normalizedStatus === "in_progress" || normalizedStatus === "running";

        const classMap = {
          IN_PROGRESS: "status-inprogress",
          PASSED: "status-passed",
          STOPPED: "status-failed",
          FAILED: "status-failed",
          NOT_STARTED: "status-pending",
          PAUSED: "status-paused",
        };

        const formatStatus = (status) => {
          const text = status.toString().toLowerCase().replace(/_/g, " ");
          return text.charAt(0).toUpperCase() + text.slice(1);
        };

        if (isInProgress) {
          return (
            <span className="d-inline-flex align-items-center opacity-50 status-badge status-inprogress">
              <span className="spinner-border spinner-border-sm me-2" />
              <span>Running...</span>
            </span>
          );
        }

        if (statusToShow == "PAUSED" || statusToShow == "Paused") {
          return (
            <span className="d-inline-flex align-items-center opacity-50 status-badge status-paused">
              <span>Paused</span>
            </span>
          );
        }

        return (
          <span className={`status-badge ${classMap[statusToShow] || ""}`}>
            {formatStatus(statusToShow)}
          </span>
        );
      },
    },
    {
      dataField: "actions",
      text: "Action",
      formatter: (cell, row) => {
        const statusToShow = row.executed_status || row.current_status;

        const normalizedStatus = statusToShow
          ? statusToShow.toString().toLowerCase().replace(/\s+/g, "_")
          : "";

        const isRunning =
          normalizedStatus === "in_progress" ||
          normalizedStatus === "running";

        const isPaused = normalizedStatus === "paused";

        // Separate loaders
        const pauseLoading =
          runningTests[row.id]?.pause ||
          runningTests[row.id]?.resume;

        const stopLoading =
          runningTests[row.id]?.stop;

        return (
          <ul className="nav gap-3 align-items-center">
            {/* View */}
            <li>
              <Link
                to={`/admin/test-run-view/${row.id}`}
                className="clr_sky text-decoration-underline"
              >
                <SlEye size={22} />
              </Link>
            </li>

            {/* Show controls only if running or paused */}
            {(isRunning || isPaused) && (
              <>
                {/* Pause / Resume */}
                <li className="mt-1">
                  {pauseLoading ? (
                    <button className="btn btn-sm btn-secondary" disabled>
                      <span className="spinner-border spinner-border-sm" />
                    </button>
                  ) : (
                    <button
                      className="btn btn-sm btn-warning"
                      onClick={() =>
                        handleTestRunAction(
                          row.id,
                          isPaused ? "resume" : "pause"
                        )
                      }
                      title={isPaused ? "Resume" : "Pause"}
                      disabled={pauseLoading}
                    >
                      <i
                        className={`fa-solid ${isPaused ? "fa-play" : "fa-pause"
                          }`}
                      ></i>
                    </button>
                  )}
                </li>

                {/* Stop */}
                <li className="mt-1">
                  {stopLoading ? (
                    <button className="btn btn-sm btn-secondary" disabled>
                      <span className="spinner-border spinner-border-sm" />
                    </button>
                  ) : (
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() =>
                        handleTestRunAction(row.id, "stop")
                      }
                      title="Stop"
                      disabled={stopLoading}
                    >
                      <i className="fa-solid fa-stop"></i>
                    </button>
                  )}
                </li>
              </>
            )}
          </ul>
        );
      },
    }

  ];
  /* ================= Execute Selected Runs ================= */
  // const executeTestRun = async () => {
  //   if (selectedTestRunIds.length === 0) return;
  //   setExecuting(true);
  //   try {
  //     console.log("runCases",runCases)
  //     // Get all folder IDs from selected test runs, handling comma-separated values
  //     const allFolderIds = runCases
  //       .filter((run) => selectedTestRunIds.includes(run.id))
  //       .flatMap((run) => parseFolderIds(run.folder_ids));

  //     // Remove duplicate folder IDs
  //     const uniqueFolderIds = [...new Set(allFolderIds)];

  //     const payload = {
  //       action: "run",
  //       folderIds: uniqueFolderIds,
  //       testCaseIds: [],
  //     };

  //     const response = await APIServices.post(
  //       "test-execution/execute",
  //       payload,
  //       headers
  //     );

  //     if (response?.status === 200 || response?.status === 201) {
  //       setSelectedTestRunIds([]);
  //       toast.success("Test Run Started Successfully");
  //       fetchRunCase(currentPage);
  //     } else {
  //       toast.error(
  //         response?.data?.detail ?? "Some error occurred, Try after sometime"
  //       );
  //     }
  //   } catch (error) {
  //     toast.error(error.message || "Error executing test run");
  //     console.error("Error executing test run:", error);
  //   }
  //   setExecuting(false);
  // };

  const executeTestRun = async () => {
    if (selectedTestRunIds.length === 0) return;

    setExecuting(true);

    try {
      // -----------------------------
      // Folder IDs (existing logic)
      // -----------------------------
      const allFolderIds = runCases
        .filter((run) => selectedTestRunIds.includes(run.id))
        .flatMap((run) => parseFolderIds(run.folder_ids));

      const uniqueFolderIds = [...new Set(allFolderIds)];

      // -----------------------------
      // NEW: Extract test_case_id
      // -----------------------------
      const allTestCaseIds = runCases
        .filter((run) => selectedTestRunIds.includes(run.id))
        .flatMap((run) =>
          (run.test_cases || []).map((tc) => tc.test_case_id)
        );

      // Remove duplicates
      const uniqueTestCaseIds = [...new Set(allTestCaseIds)];

      // -----------------------------
      // Payload
      // -----------------------------
      const payload = {
        action: "run",
        folderIds: uniqueFolderIds,
        testCaseIds: uniqueTestCaseIds,
      };
      const response = await APIServices.post(
        "test-execution/execute",
        payload,
        headers
      );

      if (response?.status === 200 || response?.status === 201) {
        setSelectedTestRunIds([]);
        toast.success("Test Run Started Successfully");
        fetchRunCase(currentPage);
      } else {
        toast.error(
          response?.data?.detail ?? "Some error occurred, Try after sometime"
        );
      }
    } catch (error) {
      toast.error(error.message || "Error executing test run");
      console.error("Error executing test run:", error);
    }

    setExecuting(false);
  };

  /* ================= Start New Test Run (Modal Submit) ================= */
  const handleStartNewTestRun = async () => {
    if (!selectedFolderId) return;

    setSubmitting(true);
    try {
      const payload = {
        action: "run",
        folderIds: [Number(selectedFolderId)],
        testCaseIds: selectedTestCases,
      };

      const response = await APIServices.post(
        "test-execution/execute",
        payload,
        headers
      );
      if (response?.status === 200 || response?.status === 201) {
        setShowModal(false);
        setSelectedFolderId("");
        setTestCases([]);
        setSelectedTestCases([]);
        toast.success("Test Run Started Successfully");
        fetchRunCase(currentPage);
      } else {
        toast.error(response?.data?.detail ?? "Some error occurred, Try after sometime");
      }
    } catch (error) {
      console.error("Error starting test run:", error);
      toast.error("Error starting test run");
    }
    setSubmitting(false);
  };

  return (
    <div className="all_content test_run_management">
      <div className="container-fluid">
        {/* ===== Header ===== */}
        <div className="head-section d-flex align-items-center justify-content-between">
          <h1 className="font_16 font-600 clr_blue">Test Run Management</h1>
          <div className="d-flex gap-2">
            <button
              className="btn sky_theme btn-sm h-40 d-flex align-items-center gap-2"
              disabled={selectedTestRunIds.length === 0 || executing}
              onClick={executeTestRun}
            >
              {executing ? (
                <>
                  <span className="spinner-border spinner-border-sm" />
                  Running...
                </>
              ) : (
                <>
                  <i className="fa-solid fa-play"></i> Test Run
                </>
              )}
            </button>

            <button
              className="btn green_theme btn-sm d-flex align-items-center gap-2"
              onClick={() => setShowModal(true)}
            >
              <i className="fa-solid fa-plus"></i> Start New Test Run
            </button>
          </div>
        </div>

        {/* ===== Search ===== */}
        <div className="search-box d-flex align-items-center gap-3 p-3">
          <input
            type="text"
            className="form-control h-40"
            placeholder="Search by folder name"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
        </div>

        {/* ===== Table ===== */}
        <div className="head-section d-flex align-items-center justify-content-between mt-2">
          <h1 className="font_16 font-600 clr_blue">History Runs</h1>
        </div>
        <div className="comman_table mt-4">

          {loading ? (
            <div className="text-center p-4">
              <div className="spinner-border text-primary" />
            </div>
          ) : (
            <PaginatedTable
              data={runCases}
              columns={columns}
              totalPages={totalPages}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              limit={limit}
              setLimit={setLimit}
              totalItems={totalItems}
            />
          )}
        </div>
      </div>

      {/* ===== React Bootstrap Modal ===== */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        size="lg"
        backdrop={false}
      >
        <Modal.Header closeButton>
          <Modal.Title className="font_16 font-500 clr_blue">
            Start New Test Run
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* Folder Selection */}
          <Form.Group className="mb-4">
            <Form.Label>Master Test Folder *</Form.Label>
            <Form.Select
              value={selectedFolderId}
              disabled={loadingFolders}
              onChange={(e) => setSelectedFolderId(e.target.value)}
            >
              <option value="">
                {loadingFolders ? "Loading folders..." : "Select folder"}
              </option>
              {folderList.map((folder) => (
                <option
                  key={folder.folder_id}
                  value={folder.folder_id}
                  disabled={folder.test_case_count === 0}
                >
                  {folder.folder_name} ({folder.test_case_count})
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {/* Test Cases List */}
          {selectedFolderId && (
            <div className="test-cases-section">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="font_14 font-600 clr_blue mb-0">
                  Test Cases ({testCases.length})
                </h6>
                {testCases.length > 0 && (
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="selectAllTestCases"
                      checked={
                        testCases.length > 0 &&
                        selectedTestCases.length === testCases.length
                      }
                      onChange={(e) => handleSelectAllTestCases(e.target.checked)}
                    />
                    <label
                      className="form-check-label small"
                      htmlFor="selectAllTestCases"
                    >
                      Select All
                    </label>
                  </div>
                )}
              </div>

              {loadingTestCases ? (
                <div className="text-center p-3">
                  <Spinner size="sm" className="me-2" />
                  Loading test cases...
                </div>
              ) : testCases.length > 0 ? (
                <div className="test-cases-list border rounded p-3" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {testCases.map((testCase) => (
                    <div key={testCase.id} className="form-check mb-2">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`testCase-${testCase.id}`}
                        checked={selectedTestCases.includes(testCase.id)}
                        onChange={(e) =>
                          handleTestCaseCheckboxChange(testCase.id, e.target.checked)
                        }
                      />
                      <label
                        className="form-check-label"
                        htmlFor={`testCase-${testCase.id}`}
                      >
                        {testCase.name}
                      </label>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center p-3 border rounded">
                  <p className="text-muted mb-0">No test cases found in this folder.</p>
                </div>
              )}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!selectedFolderId || submitting || selectedTestCases.length === 0}
            onClick={handleStartNewTestRun}
          >
            {submitting ? (
              <>
                <Spinner size="sm" className="me-2" />
                Starting...
              </>
            ) : (
              `Start Test Run (${selectedTestCases.length} selected)`
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default TestRunManagement;